'use client'

import { useCallback, useState, useEffect, useRef } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getNodeByKey } from 'lexical'
import { FiFilm, FiFileText, FiLayers } from 'react-icons/fi'
import { FaPause, FaPlay, FaStop } from 'react-icons/fa'
import { TbMoodSmile } from 'react-icons/tb'
import { LuLoaderCircle, LuSparkles } from 'react-icons/lu'

import { useShow } from '@/contexts/ShowContext'
import { useScriptAssets } from '@/contexts/ScriptAssetsContext'
import useKagamiBridge from '@/hooks/useKagamiBridge'
import { apiFetch } from '@/lib/api'
import { $extractSpeechAfterMention } from './scriptExtraction'

const VOICE_MODEL_ID = '698476656cbae33f09763f19'

const COMMAND_STYLES = {
    segment: {
        bg: 'bg-primary/10',
        border: 'border-primary/40',
        text: 'text-primary',
    },
    scene: {
        bg: 'bg-secondary/20',
        border: 'border-secondary-foreground/40',
        text: 'text-secondary-foreground',
    },
    description: {
        bg: 'bg-sky-500/20',
        border: 'border-sky-500/40',
        text: 'text-sky-300',
    },
    break: {
        bg: 'bg-orange-500/20',
        border: 'border-orange-500/40',
        text: 'text-orange-300',
    },
    emotion: {
        bg: 'bg-pink-500/20',
        border: 'border-pink-500/40',
        text: 'text-pink-300',
    },
}

const CHARACTER_STYLE = {
    bg: 'bg-indigo-500/20',
    border: 'border-indigo-500/40',
    text: 'text-indigo-300',
}

const DEFAULT_STYLE = {
    bg: 'bg-amber-500/20',
    border: 'border-amber-500/40',
    text: 'text-amber-300',
}

function getStyle(mentionType, mentionId) {
    if (mentionType === 'character') return CHARACTER_STYLE
    return COMMAND_STYLES[mentionId] || DEFAULT_STYLE
}


function SegmentChip({ style }) {
    return (
        <span
            className={`inline-flex items-center px-1.5 py-1 mx-0.5 rounded
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
            contentEditable={false}
        >
            <FiLayers className="w-3.5 h-3.5" />
        </span>
    )
}

function SceneChip({ style }) {
    return (
        <span
            className={`inline-flex items-center px-1.5 py-1 mx-0.5 rounded
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
            contentEditable={false}
        >
            <FiFilm className="w-3 h-3" />
        </span>
    )
}

function BreakChip({ editor, nodeKey, mentionData, style }) {
    const [duration, setDuration] = useState(mentionData?.duration ?? 1)

    const handleDurationChange = useCallback((e) => {
        const val = Math.max(0, parseFloat(e.target.value) || 0)
        setDuration(val)
        editor.update(() => {
            const node = $getNodeByKey(nodeKey)
            if (node) {
                const writable = node.getWritable()
                writable.__mentionData = { ...writable.__mentionData, duration: val }
            }
        })
    }, [editor, nodeKey])

    return (
        <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg text-xs font-medium
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
            contentEditable={false}
        >
            <FaPause className="w-2.5 h-2.5 opacity-70" />
            <input
                type="number"
                min="0"
                step="0.5"
                value={duration}
                onChange={handleDurationChange}
                className="w-5 bg-transparent text-center text-[12px] outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                contentEditable={false}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            />
        </span>
    )
}

function EmotionChip({ editor, nodeKey, mentionData, style }) {
    const [emotion, setEmotion] = useState(mentionData?.emotion ?? '')
    const inputRef = useRef(null)
    const measureRef = useRef(null)
    const [inputWidth, setInputWidth] = useState(40)

    useEffect(() => {
        requestAnimationFrame(() => {
            inputRef.current?.focus()
        })
    }, [])

    useEffect(() => {
        if (measureRef.current) {
            const measured = measureRef.current.scrollWidth + 4
            setInputWidth(Math.max(40, Math.min(measured, 200)))
        }
    }, [emotion])

    const handleEmotionChange = useCallback((e) => {
        const val = e.target.value
        setEmotion(val)
        editor.update(() => {
            const node = $getNodeByKey(nodeKey)
            if (node) {
                const writable = node.getWritable()
                writable.__mentionData = { ...writable.__mentionData, emotion: val }
            }
        })
    }, [editor, nodeKey])

    return (
        <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded text-xs font-medium
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
            contentEditable={false}
        >
            <TbMoodSmile className="w-3 h-3 opacity-70" />
            <span
                ref={measureRef}
                className="absolute invisible whitespace-pre text-xs"
                aria-hidden="true"
            >
                {emotion || 'emotion'}
            </span>
            <input
                ref={inputRef}
                type="text"
                value={emotion}
                onChange={handleEmotionChange}
                placeholder="emotion"
                style={{ width: inputWidth }}
                className="bg-transparent text-center text-xs outline-none placeholder:opacity-40"
                contentEditable={false}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            />
        </span>
    )
}

function CharacterChip({ editor, nodeKey, mentionId, objectId, style }) {
    const { show } = useShow()
    const { destinationFolder, requestGeneration } = useScriptAssets()
    const { runTaskAndPoll, getTask } = useKagamiBridge()
    const [generating, setGenerating] = useState(false)
    const [playing, setPlaying] = useState(false)
    const [audioUrl, setAudioUrl] = useState(null)
    const audioRef = useRef(null)
    const containerRef = useRef(null)
    const fetchedRef = useRef(false)

    const character = show?.characters?.find(c => c._id === mentionId)
    const name = character?.name || mentionId
    const thumbnailUrl = character?.thumbnail_url
    const voiceId = character?.voice_ids?.find(v => v.language === 'en')?.voice_id
        || character?.voice_ids?.[0]?.voice_id
    // The generator panel keys its character dropdown by the first registered
    // voice id, so use that here to make the character pre-select correctly.
    const presetVoiceId = character?.voice_ids?.[0]?.voice_id

    // Lazy fetch the most recent audio generation for this mention's group
    // when the chip first scrolls into view.
    useEffect(() => {
        const el = containerRef.current
        if (!el || !objectId || !show?._id) return
        if (fetchedRef.current) return

        const fetchLatest = async () => {
            if (fetchedRef.current) return
            fetchedRef.current = true
            try {
                const params = new URLSearchParams({
                    group: objectId,
                    types: 'audio',
                    size: '1',
                    page: '1',
                })
                const res = await apiFetch(`/api/shows/${show._id}/generations?${params}`)
                if (!res.ok) return
                const json = await res.json()
                const latest = json?.data?.[0]
                if (latest?.url) setAudioUrl(latest.url)
            } catch (err) {
                console.warn('Failed to fetch latest voice generation:', err)
            }
        }

        if (typeof IntersectionObserver === 'undefined') {
            fetchLatest()
            return
        }

        const observer = new IntersectionObserver(
            entries => {
                if (entries.some(e => e.isIntersecting)) {
                    observer.disconnect()
                    fetchLatest()
                }
            },
            { rootMargin: '200px 0px' },
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [objectId, show?._id])

    const handleGenerate = useCallback(async (e) => {
        e.preventDefault()
        e.stopPropagation()
        if (generating) return

        let speechText = ''
        editor.getEditorState().read(() => {
            const node = $getNodeByKey(nodeKey)
            if (node) speechText = $extractSpeechAfterMention(node)
        })

        if (!speechText) {
            console.warn('No speech text found after character mention')
            return
        }
        if (!voiceId) {
            console.warn('No voice ID found for character', name)
            return
        }

        try {
            setGenerating(true)
            const result = await runTaskAndPoll({
                modelId: VOICE_MODEL_ID,
                showId: show?._id,
                ...(objectId ? { group: objectId } : {}),
                generate_assets: true,
                destination_folder: destinationFolder || null,
                payload: {
                    prompt: speechText,
                    voiceId,
                    // voiceSettings: {} // placeholder - falls back to model defaults
                },
            })

            let url = result?.payload?.voiceUrl
            if (!url && result?._id) {
                const task = await getTask(result._id)
                url = task?.payload?.voiceUrl
            }

            if (!url) {
                console.warn('Voice generation completed but no audio URL was returned')
                return
            }

            setAudioUrl(url)
        } catch (err) {
            console.error('Voice generation failed:', err)
        } finally {
            setGenerating(false)
        }
    }, [editor, nodeKey, objectId, voiceId, name, show?._id, generating, destinationFolder, runTaskAndPoll, getTask])

    // Reveal the Assets generator panel and preset it with the ElevenLabs TTS
    // model, the speech that follows this mention, and the character's voice —
    // leaving the actual "Generate" to the user in the panel.
    const handleOpenGenerator = useCallback((e) => {
        e.preventDefault()
        e.stopPropagation()

        let speechText = ''
        editor.getEditorState().read(() => {
            const node = $getNodeByKey(nodeKey)
            if (node) speechText = $extractSpeechAfterMention(node)
        })

        requestGeneration({
            modelId: VOICE_MODEL_ID,
            settings: {
                prompt: speechText || '',
                ...(presetVoiceId ? { voiceId: presetVoiceId } : {}),
            },
        })
    }, [editor, nodeKey, presetVoiceId, requestGeneration])

    const handlePlayToggle = useCallback(async (e) => {
        e.preventDefault()
        e.stopPropagation()
        if (!audioUrl) return

        try {
            const existing = audioRef.current
            if (existing && !existing.paused && !existing.ended) {
                // Stop: pause and rewind to the start.
                existing.pause()
                try { existing.currentTime = 0 } catch { /* ignore seek errors */ }
                return
            }

            if (existing && existing.src && existing.src.endsWith(audioUrl) === false) {
                // URL changed (e.g. after re-generate) — drop the stale instance.
                existing.pause()
                audioRef.current = null
            }

            let audio = audioRef.current
            if (!audio) {
                audio = new Audio(audioUrl)
                audio.addEventListener('play', () => setPlaying(true))
                audio.addEventListener('playing', () => setPlaying(true))
                audio.addEventListener('pause', () => setPlaying(false))
                audio.addEventListener('ended', () => setPlaying(false))
                audioRef.current = audio
            } else {
                // Always start from the beginning on a fresh play.
                try { audio.currentTime = 0 } catch { /* ignore seek errors */ }
            }

            await audio.play()
        } catch (err) {
            console.error('Audio playback failed:', err)
            setPlaying(false)
        }
    }, [audioUrl])

    // If a new audio URL comes in (regenerate / lazy fetch), invalidate the
    // previously-cached <audio> instance so the next toggle uses the new one.
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current = null
            setPlaying(false)
        }
    }, [audioUrl])

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause()
                audioRef.current = null
            }
        }
    }, [])

    const canPlay = !!audioUrl && !generating

    return (
        <span
            ref={containerRef}
            className={`inline-flex items-center gap-2 rounded-md text-xs font-semibold overflow-hidden pr-2
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
            contentEditable={false}
        >
            {thumbnailUrl &&
                <img
                    src={thumbnailUrl}
                    alt={name}
                    className="w-7 h-7 object-cover"
                />
            }
            <span>{name}</span>
            <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleOpenGenerator}
                title='Generate voice'
                className='px-1 py-0.5 self-stretch text-secondary-foreground/80 hover:text-secondary-foreground hover:bg-secondary-foreground/10 cursor-pointer flex items-center justify-center'
            >
                <LuSparkles className='w-2.5 h-2.5' />
            </button>
        </span>
    )
}

function DescriptionChip({ style }) {
    return (
        <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded text-xs font-medium
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
            contentEditable={false}
        >
            <FiFileText className="w-3 h-3 opacity-70" />
            <span>Description</span>
        </span>
    )
}


export default function MentionComponent({ mentionType, mentionId, mentionName, mentionData, nodeKey, objectId }) {
    const [editor] = useLexicalComposerContext()
    const style = getStyle(mentionType, mentionId)

    if (mentionType === 'character') {
        return <CharacterChip editor={editor} nodeKey={nodeKey} mentionId={mentionId} objectId={objectId} style={style} />
    }

    if (mentionId === 'segment') {
        return <SegmentChip style={style} />
    }

    if (mentionId === 'scene') {
        return <SceneChip style={style} />
    }

    if (mentionId === 'break') {
        return <BreakChip editor={editor} nodeKey={nodeKey} mentionData={mentionData} style={style} />
    }

    if (mentionId === 'emotion') {
        return <EmotionChip editor={editor} nodeKey={nodeKey} mentionData={mentionData} style={style} />
    }

    if (mentionId === 'description') {
        return <DescriptionChip style={style} />
    }


    return (
        <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded text-xs font-medium
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
            contentEditable={false}
        >
            <span>{mentionName}</span>
        </span>
    )
}
